import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  enableProdMode,
  EnvironmentProviders,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  Provider,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withRouterConfig
} from '@angular/router';
import { QuillModule } from 'ngx-quill';

import { APP_PROVIDERS } from '@app/app.providers';
import { APP_ROUTES } from '@app/app.routes';
import { CORE_PROVIDERS } from '@core/core.providers';
import { CLIENT_INTERCEPTOR } from '@core/interceptors/client.interceptor';
import { PROJECT_INTERCEPTOR } from '@core/interceptors/project.interceptor';
import { SAVING_INTERCEPTOR } from '@core/interceptors/saving.interceptor';
import { UNAUTHORIZED_INTERCEPTOR } from '@core/interceptors/unauthorized.interceptor';
import { MATERIAL_PROVIDERS } from '@shared/material/material.providers';
import { AppInitializerService } from '@shared/services/app-initializer.service';
import { SVG_ICONS_PROVIDERS } from '@shared/svg-icons/svg-icons.providers';
import { AUTH_PROVIDERS, MSAL_INTERCEPTOR } from '@auth/auth.providers';
import { SETTINGS_PROVIDERS } from '@settings/settings.providers';
import { FILE_UPLOAD_PROVIDERS } from '@file-upload/file-upload.providers';
import { NOTIFICATIONS_PROVIDERS } from '@notifications/notifications.providers';
import { FLOORPLANS_PROVIDERS } from '@floorplans/floorplans.providers';
import { ZENDESK_PROVIDERS } from '@zendesk/zendesk.providers';

interface Environment {
  production: boolean;
}

export function getAppProviders(
  environment: Environment
): (EnvironmentProviders | Provider)[] {
  if (environment.production) {
    enableProdMode();
  }

  return [
    provideRouter(
      APP_ROUTES,
      withComponentInputBinding(),
      withRouterConfig({
        paramsInheritanceStrategy: 'always'
      })
    ),
    provideAnimations(),
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideHttpClient(
      withInterceptors([
        MSAL_INTERCEPTOR,
        CLIENT_INTERCEPTOR,
        PROJECT_INTERCEPTOR,
        UNAUTHORIZED_INTERCEPTOR,
        SAVING_INTERCEPTOR
      ])
    ),
    provideAppInitializer(() => inject(AppInitializerService).init()),
    importProvidersFrom(QuillModule.forRoot()),
    ...APP_PROVIDERS,
    ...CORE_PROVIDERS,
    ...AUTH_PROVIDERS,
    ...MATERIAL_PROVIDERS,
    ...FILE_UPLOAD_PROVIDERS,
    ...NOTIFICATIONS_PROVIDERS,
    ...SETTINGS_PROVIDERS,
    ...SVG_ICONS_PROVIDERS,
    ...FLOORPLANS_PROVIDERS,
    ...ZENDESK_PROVIDERS
  ];
}
